// Browser Web Speech API mic button. Single-shot capture (start → stop on
// `onend`), then handed off to the parent. No streaming partials — the LLM
// parser wants the full utterance.
//
// Safari + Chrome support webkitSpeechRecognition; Firefox does not. We
// gracefully hide the button when the API isn't available.

import { useEffect, useRef, useState } from 'react';

export default function VoiceInput({ onTranscript, prompt = '🎤 Voice', busy = false }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const [err, setErr] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
    if (!SR) return;
    const r = new SR();
    r.continuous = false;
    r.interimResults = false;
    r.lang = 'en-US';
    r.onresult = (ev) => {
      const transcript = Array.from(ev.results).map(res => res[0].transcript).join(' ').trim();
      if (transcript) onTranscript(transcript);
    };
    r.onerror = (ev) => { setErr(ev.error || 'speech error'); setListening(false); };
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    return () => { try { r.stop(); } catch {} };
  }, [onTranscript]);

  function toggle() {
    setErr('');
    if (!recognitionRef.current) return;
    if (listening) { recognitionRef.current.stop(); return; }
    try { recognitionRef.current.start(); setListening(true); }
    catch (e) { setErr(e.message); }
  }

  if (!supported) return null;

  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" onClick={toggle} disabled={busy}
              className={`btn ${listening ? 'bg-rust-500 text-cream-50 animate-pulse' : 'btn-ghost'}`}>
        {listening ? '⏺ Listening — tap to stop' : prompt}
      </button>
      {err && <span className="text-xs text-rust-700">{err}</span>}
    </span>
  );
}
